import * as React from "react"
import { useIntl } from "react-intl"
import { useFormat } from "@/lib/format"

export type TrendPoint = { id: number; score: number; date: string; title: string }

const HEIGHT = 200
const MARGIN = { top: 16, right: 44, bottom: 28, left: 36 }
const Y_TICKS = [0, 25, 50, 75, 100]
/** Minimum horizontal room per x-axis date label before labels are thinned. */
const LABEL_SPACE = 56

/**
 * Practice-exam scores over time: one series, so no legend (the section title
 * names it). Hover or focus a point for its details; the table view below
 * carries every value without hovering.
 */
export function ScoreTrendChart({ points }: { points: TrendPoint[] }) {
  const intl = useIntl()
  const format = useFormat()
  const shortDate = (iso: string) => format.date(iso, { month: "short", day: "numeric" })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [width, setWidth] = React.useState(640)
  const [active, setActive] = React.useState<number | null>(null)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry.contentRect.width))))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const plotW = width - MARGIN.left - MARGIN.right
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom
  const x = (i: number) => MARGIN.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW)
  const y = (score: number) => MARGIN.top + plotH - (score / 100) * plotH
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.score)}`).join(" ")
  const labelEvery = Math.max(1, Math.ceil((points.length * LABEL_SPACE) / Math.max(plotW, 1)))
  const last = points.length - 1

  const nearest = (clientX: number, svg: SVGSVGElement) => {
    const left = svg.getBoundingClientRect().left
    let best = 0
    points.forEach((_, i) => {
      if (Math.abs(x(i) - (clientX - left)) < Math.abs(x(best) - (clientX - left))) best = i
    })
    return best
  }

  const activePoint = active === null ? null : points[active]

  return (
    <div ref={containerRef} className="relative">
      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label={intl.formatMessage({ id: "dashboard.chart_label" }, { count: points.length, score: points[last].score })}
        onPointerMove={(e) => setActive(nearest(e.clientX, e.currentTarget))}
        onPointerLeave={() => setActive(null)}
        className="block touch-none"
      >
        {Y_TICKS.map((t) => (
          <g key={t}>
            <line
              x1={MARGIN.left}
              x2={width - MARGIN.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--color-hairline)"
              strokeWidth={1}
            />
            <text x={MARGIN.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-ink-muted text-[11px] tabular-nums">
              {t}
            </text>
          </g>
        ))}
        {points.map((p, i) =>
          i % labelEvery === 0 || i === last ? (
            <text key={p.id} x={x(i)} y={HEIGHT - 8} textAnchor="middle" className="fill-ink-muted text-[11px]">
              {shortDate(p.date)}
            </text>
          ) : null,
        )}

        {activePoint && (
          <line
            x1={x(active!)}
            x2={x(active!)}
            y1={MARGIN.top}
            y2={MARGIN.top + plotH}
            stroke="var(--color-ink-muted)"
            strokeWidth={1}
          />
        )}

        {points.length > 1 && (
          <path d={path} fill="none" stroke="var(--color-chart-strong)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        )}
        {points.map((p, i) => (
          <g key={p.id}>
            <circle
              cx={x(i)}
              cy={y(p.score)}
              r={active === i ? 5 : 4}
              fill="var(--color-chart-strong)"
              stroke="var(--color-page)"
              strokeWidth={2}
            />
            {/* Larger invisible hit area, also the keyboard focus target. */}
            <circle
              cx={x(i)}
              cy={y(p.score)}
              r={12}
              fill="transparent"
              tabIndex={0}
              role="button"
              aria-label={intl.formatMessage({ id: "dashboard.chart_point" }, { date: shortDate(p.date), score: p.score, title: p.title })}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
              className="outline-none focus-visible:stroke-accent"
              strokeWidth={2}
            />
          </g>
        ))}
        <text
          x={x(last) + 10}
          y={y(points[last].score)}
          dy="0.32em"
          className="fill-ink-display text-xs font-semibold"
        >
          {points[last].score}%
        </text>
      </svg>

      {activePoint && (
        <div
          className="pointer-events-none absolute z-10 rounded-xl border border-hairline bg-page px-3 py-2 text-xs shadow-sm"
          style={{
            left: Math.min(Math.max(x(active!) - 80, 0), width - 160),
            // Above the point when there's room, otherwise below it so it never covers the point.
            top: y(activePoint.score) - 64 >= 0 ? y(activePoint.score) - 64 : y(activePoint.score) + 14,
            width: 160,
          }}
          role="status"
        >
          <div className="flex items-center gap-2">
            <span className="h-0.5 w-3 rounded-full bg-chart-strong" aria-hidden />
            <span className="text-sm font-semibold text-ink-display">{activePoint.score}%</span>
          </div>
          <div className="mt-0.5 truncate text-ink-muted">
            {shortDate(activePoint.date)} · {activePoint.title}
          </div>
        </div>
      )}
    </div>
  )
}
